import moment from 'moment';

import { TIME_DISPLAY_FORMAT, TIME_INPUT_FORMAT } from './constants';
import type { DateRangePreset, DateRangeValue } from './index';
import { presetLabel, presetValue } from './utils';

// Named preset constants. Prefer these over positional indexing of the preset
// arrays so that new presets can be added without renumbering every caller.
export const FIVE_MINUTE_PRESET: DateRangePreset = { unit: 'minutes', quantity: 5 };
export const FIFTEEN_MINUTE_PRESET: DateRangePreset = { unit: 'minutes', quantity: 15 };
export const ONE_HOUR_PRESET: DateRangePreset = { unit: 'minutes', quantity: 60 };
export const FOUR_HOUR_PRESET: DateRangePreset = { unit: 'hours', quantity: 4 };
export const ONE_DAY_PRESET: DateRangePreset = { unit: 'hours', quantity: 24 };
export const SEVEN_DAY_PRESET: DateRangePreset = { unit: 'days', quantity: 7 };
export const THIRTY_DAY_PRESET: DateRangePreset = { unit: 'days', quantity: 30 };
export const THREE_MONTH_PRESET: DateRangePreset = { unit: 'months', quantity: 3 };

export const DEFAULT_TIME_PRESETS: DateRangePreset[] = [
  FIVE_MINUTE_PRESET,
  FIFTEEN_MINUTE_PRESET,
  ONE_HOUR_PRESET,
  FOUR_HOUR_PRESET,
  ONE_DAY_PRESET,
  SEVEN_DAY_PRESET,
  THIRTY_DAY_PRESET,
];

export const EXTENDED_TIME_PRESETS: DateRangePreset[] = [...DEFAULT_TIME_PRESETS, THREE_MONTH_PRESET];

export const formatDisplayedDate = (date?: Date) => {
  if (!date) {
    return '';
  }
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export const getTimeStringFromDate = (date?: Date, endDate?: boolean): string => {
  if (!date) {
    return endDate ? '11:59pm' : '12:00 AM';
  }

  return moment(date).format(TIME_DISPLAY_FORMAT);
};

export const getTimeInfo = (timeString: string) => {
  const date = moment(timeString, TIME_INPUT_FORMAT);
  return {
    hour: date.format('HH'),
    minute: date.minute(),
    timeOfDay: date.format('a'),
    hour24: date.hour(),
  };
};

export const isPresetSelected = (preset: DateRangePreset, selectedPreset?: DateRangePreset) => {
  if (!selectedPreset) {
    return false;
  }

  return presetValue(selectedPreset) === presetValue(preset);
};

export const isCustomSelected = ({
  presets,
  selectedValue,
}: {
  presets: DateRangePreset[];
  selectedValue: DateRangeValue;
}) => {
  const foundPreset = presets.find((preset) => isPresetSelected(preset, selectedValue.selectedPreset));

  return !foundPreset;
};

const toDateTimeString = (date: Date, showYear: boolean) => {
  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  };

  if (showYear) {
    options.year = 'numeric';
  }
  return date.toLocaleDateString('en-us', options);
};

export const setTimeOnDate = (date: Date, hour: number, minute: number) => {
  const newDate = new Date(date);

  newDate.setHours(hour);
  newDate.setMinutes(minute);

  return newDate;
};

export const getInputLabel = ({
  selectedValue,
  presets,
}: {
  selectedValue: DateRangeValue;
  presets: DateRangePreset[];
}) => {
  if (selectedValue.isLiveMode) {
    return 'Live mode';
  }

  const foundPreset = presets.find((preset) => isPresetSelected(preset, selectedValue.selectedPreset));

  if (foundPreset) {
    return presetLabel(foundPreset);
  }

  const { startDate, endDate } = selectedValue;

  if (startDate && endDate) {
    const showYear = endDate.getFullYear() > startDate.getFullYear();

    return `${toDateTimeString(startDate, showYear)} - ${toDateTimeString(endDate, showYear)}`;
  }

  return '';
};
